import { supabase } from './supabaseClient';

/**
 * Helper function to send emails via the default organization email account.
 * Calls send-org-email, which picks Gmail vs Outlook server-side (no public account view).
 * 
 * @param {Object} params - Email parameters
 * @param {string} params.to - Recipient email address
 * @param {string} params.subject - Email subject
 * @param {string} params.htmlBody - HTML email content
 * @param {string} [params.textBody] - Plain text email content (optional)
 * @param {string} [params.userId] - User ID for audit purposes
 * @param {string|string[]} [params.cc] - CC recipients (optional)
 * @param {string|string[]} [params.bcc] - BCC recipients (optional)
 * @param {Array} [params.attachments] - Array of attachment objects {name, contentType, contentBytes} (optional)
 * @returns {Promise<Object>} Response from the function
 */
export const sendOutlookEmail = async ({
  to,
  subject,
  htmlBody,
  textBody,
  userId = 'system', // User ID for audit purposes
  cc,
  bcc,
  attachments,
}) => {
  try {
    const payload = {
      userId,
      to,
      subject,
    };

    // Use textBody if provided, otherwise use htmlBody
    if (textBody) {
      payload.textBody = textBody;
    } else {
      payload.htmlBody = htmlBody;
    }

    // Add optional fields if provided
    if (cc) payload.cc = cc;
    if (bcc) payload.bcc = bcc;
    if (attachments) payload.attachments = attachments;

    console.log('📧 Sending email via send-org-email');

    const { data, error } = await supabase.functions.invoke('send-org-email', {
      body: payload,
    });

    if (error) {
      console.error('Error sending email via send-org-email:', error);
      throw error;
    }

    if (data?.success === false) {
      throw new Error(data?.error || 'Failed to send email');
    }

    return {
      success: true,
      data,
      provider: data?.provider,
      from: data?.from,
    };
  } catch (error) {
    console.error('Failed to send email:', error);
    return {
      success: false,
      error: error.message || 'Failed to send email',
    };
  }
};

/**
 * Format date for display: "07 Feb 2026 (Sat)"
 */
const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayOfWeek = days[date.getDay()];
    return `${day} ${month} ${year} (${dayOfWeek})`;
  } catch {
    return dateString;
  }
};

/**
 * Format URL to display as full text instead of embedded link
 * @param {string} url - The URL to format
 * @returns {string} HTML paragraph with styled URL display
 */
const formatUrlAsText = (url) => {
  if (!url) return '';
  return `<p style="word-break: break-all; font-family: monospace; background-color: #f5f5f5; padding: 10px; border: 1px solid #ddd; margin: 10px 0;">${url}</p>`;
};

/**
 * Build dates table as HTML from sessions array
 * @param {Array} sessions - Array of session objects with date, start_time, end_time, venue, room
 * @returns {string} HTML table string
 */
const buildDatesTableHTML = (sessions) => {
  if (!sessions || sessions.length === 0) {
    return '<p>Schedule details will be provided separately.</p>';
  }

  const rows = sessions.map(session => {
    const date = formatDate(session.date || session.session_date);
    const startTime = session.start_time ? 
      (typeof session.start_time === 'string' && session.start_time.includes(':') 
        ? session.start_time 
        : `${session.start_time.substring(0, 2)}:${session.start_time.substring(2)}`) 
      : '';
    const endTime = session.end_time ? 
      (typeof session.end_time === 'string' && session.end_time.includes(':') 
        ? session.end_time 
        : `${session.end_time.substring(0, 2)}:${session.end_time.substring(2)}`) 
      : '';
    const timeStr = startTime && endTime ? `${startTime} - ${endTime}` : startTime || endTime || 'TBC';
    const venue = session.venue || session.custom_venue || session.venue_id || '';
    const room = session.room || session.custom_room || session.room_id || '';
    const location = [venue, room].filter(Boolean).join(', ') || 'TBC';
    
    return { date, time: timeStr, location };
  });

  let tableHTML = `
    <table style="width: 100%; border-collapse: collapse; margin: 20px 0; font-family: Arial, sans-serif;">
      <thead>
        <tr style="background-color: #f5f5f5;">
          <th style="padding: 12px; text-align: left; border: 1px solid #ddd; font-weight: bold;">Date</th>
          <th style="padding: 12px; text-align: left; border: 1px solid #ddd; font-weight: bold;">Time</th>
          <th style="padding: 12px; text-align: left; border: 1px solid #ddd; font-weight: bold;">Venue</th>
        </tr>
      </thead>
      <tbody>
  `;
  
  rows.forEach(row => {
    tableHTML += `
        <tr>
          <td style="padding: 10px; border: 1px solid #ddd;">${row.date}</td>
          <td style="padding: 10px; border: 1px solid #ddd;">${row.time}</td>
          <td style="padding: 10px; border: 1px solid #ddd;">${row.location}</td>
        </tr>
    `;
  });
  
  tableHTML += `
      </tbody>
    </table>
  `;
  
  return tableHTML;
};

/**
 * Send registration received email to participant
 * This is a wrapper that formats the email for registration received
 * 
 * Note: All URLs in emails are displayed as full text (not embedded links) for better visibility.
 * Use formatUrlAsText() helper function if you need to add URLs to email content.
 * 
 * @param {Object} params - Registration email parameters
 * @param {string} params.participantEmail - Participant email address
 * @param {string} params.participantName - Participant name
 * @param {string} params.courseName - Course name
 * @param {string} params.startDate - Course start date
 * @param {string} params.endDate - Course end date (optional)
 * @param {string} params.registrationType - Registration type (e.g., "Self-Sponsored", "Company-Sponsored")
 * @param {boolean} params.isFullCertification - Whether this is a full certification
 * @param {Array} [params.modules] - Array of modules for full certification (optional)
 * @param {Array} [params.sessions] - Array of session objects for individual course (optional)
 * @param {string} [params.registrationId] - Registration ID to update after email sent (optional)
 * @param {Array} [params.attachments] - Array of attachment objects {name, contentType, contentBytes} (optional)
 * @returns {Promise<Object>} Response from the function
 */
export const sendRegistrationEmail = async ({
  participantEmail,
  participantName,
  courseName,
  startDate,
  endDate,
  registrationType,
  isFullCertification = false,
  modules = [],
  sessions = [],
  registrationId = null,
  attachments = [],
}) => {
  // Skip company-sponsored emails (same as Pipedream logic)
  if (registrationType === 'Company-Sponsored') {
    return {
      success: true,
      skipped: true,
      message: 'Company-sponsored registration, skipping email',
    };
  }

  let htmlBody = '';
  let subject = '';

  if (isFullCertification && modules.length > 0) {
    // Full certification email - HTML
    subject = `Registration Received - ${courseName}`;
    
    // Build modules table as HTML
    const moduleRows = modules.map(module => ({
      name: module.name || 'N/A',
      startDate: formatDate(module.start_date),
      endDate: formatDate(module.end_date),
    }));

    let modulesTableHTML = `
      <table style="width: 100%; border-collapse: collapse; margin: 20px 0; font-family: Arial, sans-serif;">
        <thead>
          <tr style="background-color: #f5f5f5;">
            <th style="padding: 12px; text-align: left; border: 1px solid #ddd; font-weight: bold;">Module Name</th>
            <th style="padding: 12px; text-align: left; border: 1px solid #ddd; font-weight: bold;">Start Date</th>
            <th style="padding: 12px; text-align: left; border: 1px solid #ddd; font-weight: bold;">End Date</th>
          </tr>
        </thead>
        <tbody>
    `;
    
    moduleRows.forEach(row => {
      modulesTableHTML += `
          <tr>
            <td style="padding: 10px; border: 1px solid #ddd;">${row.name}</td>
            <td style="padding: 10px; border: 1px solid #ddd;">${row.startDate}</td>
            <td style="padding: 10px; border: 1px solid #ddd;">${row.endDate}</td>
          </tr>
      `;
    });
    
    modulesTableHTML += `
        </tbody>
      </table>
    `;

    htmlBody = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      font-family: Arial, sans-serif;
      font-size: 14px;
      line-height: 1.6;
      color: #000000;
      background-color: #ffffff;
      margin: 0;
      padding: 20px;
    }
    p {
      margin: 1em 0;
    }
    .url-display {
      word-break: break-all;
      font-family: monospace;
      background-color: #f5f5f5;
      padding: 10px;
      border: 1px solid #ddd;
      display: block;
      margin: 10px 0;
    }
  </style>
</head>
<body>
  <h2>Registration Received</h2>
  
  <p>Hi ${participantName || 'Participant'},</p>
  
  <p>Thank you for registering for ${courseName || 'this course'}. We have received your submission.</p>
  
  <p>You will receive a further update on confirmation or rejection soon. If you have any questions, simply reply to this email and we'll assist you.</p>
  
  <h3>Your Registered Modules</h3>
  
  ${modulesTableHTML}
  
  <p>Best Regards,<br/>Admin Team</p>
</body>
</html>`;
  } else {
    // Individual course email - HTML
    const courseStartEnd = startDate ? 
      `${formatDate(startDate)}${endDate ? ' to ' + formatDate(endDate) : ''}` : 
      '';
    
    // Build dates table from sessions if available
    const datesTable = sessions && sessions.length > 0 
      ? buildDatesTableHTML(sessions)
      : courseStartEnd 
        ? `<p><strong>Course Start/End:</strong> ${courseStartEnd}</p><p>All Dates and Times: Schedule details will be provided separately.</p>`
        : '<p>Schedule details will be provided separately.</p>';

    subject = `Registration received – ${courseName}`;

    htmlBody = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      font-family: Arial, sans-serif;
      font-size: 14px;
      line-height: 1.6;
      color: #000000;
      background-color: #ffffff;
      margin: 0;
      padding: 20px;
    }
    p {
      margin: 1em 0;
    }
    .url-display {
      word-break: break-all;
      font-family: monospace;
      background-color: #f5f5f5;
      padding: 10px;
      border: 1px solid #ddd;
      display: block;
      margin: 10px 0;
    }
  </style>
</head>
<body>
  <h2>Registration Received</h2>
  
  <p>Hi ${participantName || 'Participant'},</p>
  
  <p>Thank you for registering for ${courseName}. We have received your submission.</p>
  
  <p>You will receive a further update on confirmation or rejection soon. If you have any questions, simply reply to this email and we'll assist you.</p>
  
  <h3>Course Schedule</h3>
  
  ${datesTable}
  
  <p>Best Regards,<br/>Admin Team</p>
</body>
</html>`;
  }

  // Send email as HTML
  const emailResult = await sendOutlookEmail({
    to: participantEmail,
    subject,
    htmlBody,
    attachments,
  });

  // Update registration table if email was sent successfully and registrationId provided
  if (emailResult.success && registrationId) {
    try {
      const now = new Date().toISOString();
      const { error: updateError } = await supabase
        .from('registration')
        .update({
          enrollment_email_isSent: true,
          enrollment_email_datetime: now,
        })
        .eq('id', registrationId);

      if (updateError) {
        console.error('Error updating registration email status:', updateError);
        // Don't fail the whole operation if update fails
      }
    } catch (error) {
      console.error('Error updating registration email status:', error);
    }
  }

  return emailResult;
};

